import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useTranslation } from 'react-i18next';
import { Briefcase, ArrowLeft, AlertCircle, Search, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { SERVICE_CATEGORIES } from '@/config/serviceCategories';

// Service category images
import veterinarianImg from '@/assets/services/veterinarian.jpg';
import veterinaryClinicImg from '@/assets/services/veterinary-clinic.jpg';
import emergencyImg from '@/assets/services/emergency.jpg';
import groomingImg from '@/assets/services/grooming.jpg';
import dogWalkerImg from '@/assets/services/dog-walker.jpg';
import petStoreImg from '@/assets/services/pet-store.jpg';
import daycareImg from '@/assets/services/daycare.jpg';
import petHotelImg from '@/assets/services/pet-hotel.jpg';
import trainingImg from '@/assets/services/training.jpg';

const SERVICE_IMAGES: Record<string, string> = {
  veterinarian: veterinarianImg,
  veterinary_clinic: veterinaryClinicImg,
  emergency: emergencyImg,
  grooming: groomingImg,
  dog_walker: dogWalkerImg,
  pet_store: petStoreImg,
  daycare: daycareImg,
  pet_hotel: petHotelImg,
  training: trainingImg,
};

export default function Services() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isProvider, setIsProvider] = useState(false);

  useEffect(() => {
    const checkProviderStatus = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('service_providers')
        .select('id')
        .eq('user_id', user.id)
        .single();

      setIsProvider(!!data);
    };

    checkProviderStatus();
  }, [user]);

  const handleSearch = (type: string, emergency: boolean = false) => {
    navigate(`/services/search?type=${type}&emergency=${emergency}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/30 to-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </Button>
          </div>
          
          <div className="flex items-center justify-center gap-3 mb-2">
            <Briefcase className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-center text-foreground">
              {t('services.title')}
            </h1>
          </div>
          <p className="text-center text-muted-foreground max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>
      </div>

      {isProvider && (
        <div className="container mx-auto px-4 pt-6">
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-blue-900 dark:text-blue-100">
                {t('services.providerAlert')}
              </span>
              <Button 
                size="sm" 
                onClick={() => navigate('/provider-dashboard')}
                className="ml-4"
              >
                {t('services.goToDashboard')}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Service Categories Grid */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICE_CATEGORIES.map((category) => {
            const image = SERVICE_IMAGES[category.id];
            
            return (
              <Card
                key={category.id}
                className="group overflow-hidden border border-border/60 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer bg-card"
                onClick={() => handleSearch(category.type, category.emergency)}
              >
                {/* Image Section */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={image}
                    alt={t(category.titleKey)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  
                  {/* Emergency badge */}
                  {category.emergency && (
                    <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg animate-pulse">
                      24/7
                    </div>
                  )}

                  {/* Category icon floating */}
                  <div className={`absolute bottom-3 left-3 w-10 h-10 rounded-xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md`}>
                    <category.lucideIcon className={`h-5 w-5 ${category.iconColor}`} />
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-4">
                  <h3 className="text-base font-bold text-foreground mb-1">
                    {t(category.titleKey)}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {t(category.descKey)}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSearch(category.type, category.emergency);
                    }}
                  >
                    <Search className="w-4 h-4" />
                    {t('services.searchButton')}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Browse All Services */}
      <div className="container mx-auto px-4 pb-6">
        <Card className="bg-card p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-1 text-foreground">
                {t('services.browseAllServices')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('services.browseAllServicesDescription')}
              </p>
            </div>
            <Button 
              onClick={() => navigate('/services/search?type=')}
              variant="outline"
              className="gap-2 flex-shrink-0"
            >
              {t('services.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      </div>

      {/* Register as Provider Banner */}
      <div className="container mx-auto px-4 pb-12">
        <Card className="bg-gradient-to-r from-pet-purple to-pet-blue text-white p-8 text-center overflow-hidden relative">
          <div className="relative z-10">
            <h2 className="text-2xl font-bold mb-3">
              {t('services.registerProvider.banner.title')}
            </h2>
            <p className="mb-6 text-white/90 max-w-lg mx-auto">
              {t('services.registerProvider.banner.description')}
            </p>
            <Button 
              size="lg"
              variant="secondary"
              onClick={() => navigate('/register-provider')}
              className="font-semibold"
            >
              {t('services.registerProvider.banner.button')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
